import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    COLLECTION_MAX_DEPTH,
    type Collection,
    type ContentOwnership,
} from '@gitbook/app-database';
import { toast } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { apiClient, apiQuery } from '../../state';
import {
    type DNDItem,
    type DNDItemCollection,
    type DNDItemSpace,
    type DroppableState,
    useDroppableWithCallback,
} from '../DragAndDrop';
import { getCollectionFromOrgAllContent, getSpaceFromOrgAllContent } from '../Organization';

interface DropInOwnerOptions {
    position?: api.ContentPosition;
    accept?: Array<'space' | 'collection' | 'page'>;
}

/**
 * Hook to handle drop of content in a collection.
 */
export function useDropInCollection(
    collection: Collection,
    options: DropInOwnerOptions = {}
): DroppableState {
    return useDropInOwner(
        { organization: collection.organization, collection: collection.key },
        options
    );
}

/**
 * Hook to handle drop of content in an owner.
 */
export function useDropInOwner(
    owner: ContentOwnership,
    options: DropInOwnerOptions = {}
): DroppableState {
    const { data: allContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId: owner.organization })
    );

    return useDroppableWithCallback({
        shouldAccept: (item) => {
            // Restrict to spaces, collections and pages
            if (!isContentItem(item)) {
                return false;
            }

            // Further restrict to a subset of content items, if specified via options
            if (options.accept && !options.accept.includes(item.type)) {
                return false;
            }

            if (item.type === 'space') {
                const space = getSpaceFromOrgAllContent(allContent, item.id);

                if (!space) {
                    return false;
                }

                return space.item.space.permissions.admin;
            }

            const collection = getCollectionFromOrgAllContent(allContent, item.id);

            if (!collection) {
                return false;
            }

            const isAllowedToOrganize = collection.item.collection.permissions.admin;
            return (
                isAllowedToOrganize &&
                !isDropTargetDescendantCollection(item, owner, allContent) &&
                // Disallow dropping a collection deeper than 3 levels
                canInsertInOwner(owner, allContent)
            );
        },
        onDrop: async (item) => {
            if (item.type === 'space') {
                const space = getSpaceFromOrgAllContent(allContent, item.id);

                if (!space) {
                    return;
                }

                await toast.catch(async () => {
                    await apiClient.spaces.moveSpace(space.item.space.id, {
                        parent: owner.collection || null,
                        position: options.position,
                    });
                    analytics.logEvent(
                        analytics.createContentMoveEvent(space.item.space, owner, 'drag-drop', {
                            type: 'space_move',
                            space: item.id,
                            organization: owner.organization,
                        })
                    );
                }, 'Error while moving space');
            } else if (item.type === 'collection') {
                const collection = getCollectionFromOrgAllContent(allContent, item.id);

                if (!collection) {
                    return;
                }

                await toast.catch(async () => {
                    await apiClient.collections.moveCollection(collection.item.collection.id, {
                        parent: owner.collection || null,
                        position: options.position,
                    });

                    analytics.logEvent(
                        analytics.createContentMoveEvent(
                            collection.item.collection,
                            owner,
                            'drag-drop',
                            {
                                type: 'collection_move',
                                collection: item.id,
                                organization: owner.organization,
                            }
                        )
                    );
                }, 'Error while moving collection');
            }
        },
    });
}

function isContentItem(item: DNDItem): item is DNDItemSpace | DNDItemCollection {
    return ['space', 'collection'].includes(item.type);
}

export type CreateInOwnerFns = {
    /** Create a team space. If undefined, the user does not have permission. */
    createSpace:
        | ((options?: {
              /**
               * Specify a title for the created space.
               * If not specified, the space will be created with "Untitled".
               */
              withTitle?: string;
              /**
               * Navigate to the created space after creation.
               * @default true
               */
              withNavigate?: boolean;
              /**
               * Source of the space creation, used for analytics.
               */
              source?: string;
              /**
               * Collection to create the space in.
               */
              collectionId?: string;
          }) => Promise<api.Space>)
        | null;
    /** Create a collection. If undefined, the user does not have permission. */
    createCollection: ((args?: { title?: string | undefined }) => Promise<api.Collection>) | null;
};

/**
 * Return callbacks to create space or collections in an owner.
 */
export function useCreateInOwner(owner: ContentOwnership): CreateInOwnerFns {
    const { navigate } = useNavigation();

    const [{ data: allContent }, { data: organization }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationAllContent', { organizationId: owner.organization }),
            apiQuery('getOrganizationById', { organizationId: owner.organization }),
        ],
    });

    let isAllowedToCreateSpaceOrCollection = false;
    if (owner.collection) {
        const collection = getCollectionFromOrgAllContent(allContent, owner.collection);
        isAllowedToCreateSpaceOrCollection =
            collection?.item.collection.permissions.create ?? false;
    } else {
        isAllowedToCreateSpaceOrCollection = organization.permissions.createContent;
    }

    const canCreateCollection = React.useMemo(() => {
        return (
            isAllowedToCreateSpaceOrCollection &&
            canInsertInOwner(
                {
                    organization: owner.organization,
                    collection: owner.collection,
                },
                allContent
            )
        );
    }, [allContent, owner.organization, owner.collection, isAllowedToCreateSpaceOrCollection]);

    return React.useMemo<CreateInOwnerFns>(() => {
        return {
            createSpace: isAllowedToCreateSpaceOrCollection
                ? async (options) => {
                      const withNavigate = options?.withNavigate ?? true;

                      const space = await (async () => {
                          try {
                              const response = await apiClient.spaces.createSpace(
                                  owner.organization,
                                  {
                                      ...(options?.withTitle ? { title: options.withTitle } : {}),
                                      // When importing, we want to create the space with no content
                                      ...(options?.source === 'import' ? { empty: true } : {}),
                                      parent: options?.collectionId ?? owner.collection,
                                  }
                              );
                              return response.data;
                          } catch (error) {
                              toast.error('Error while creating space', error);
                              throw error;
                          }
                      })();

                      analytics.logEvent(
                          analytics.createSpaceEvent(space, {
                              type: 'space_create',
                              source: options?.source,
                          })
                      );

                      if (withNavigate) {
                          navigate({
                              screen: {
                                  type: 'space',
                                  space: space.id,
                                  organization: owner.organization,
                                  page: '',
                              },
                          });
                      }

                      return space;
                  }
                : null,
            createCollection: canCreateCollection
                ? async (args) => {
                      const collection = await (async () => {
                          try {
                              const response = await apiClient.collections.createCollection(
                                  owner.organization,
                                  {
                                      parent: owner.collection,
                                      title: args?.title,
                                  }
                              );
                              return response.data;
                          } catch (error) {
                              toast.error('Error while creating collection', error);
                              throw error;
                          }
                      })();

                      analytics.logEvent(
                          analytics.createCollectionEvent(collection, {
                              type: 'collection_create',
                              restricted: false,
                          })
                      );

                      return collection;
                  }
                : null,
        };
    }, [
        owner.organization,
        owner.collection,
        navigate,
        canCreateCollection,
        isAllowedToCreateSpaceOrCollection,
    ]);
}

/**
 * Check if the drop target is a descendant Collection of the dragged Collection item.
 * This check is designed to prevent a Parent collection to be dropped into one of its descendant.
 */
function isDropTargetDescendantCollection(
    item: DNDItemCollection,
    target: ContentOwnership,
    allContent: api.OrganizationAllContent
) {
    // Target is the DND Collection (not allowed)
    if (item.id === target.collection) {
        return true;
    }

    const dndCollection = getCollectionFromOrgAllContent(allContent, item.id);
    if (!dndCollection) {
        return false;
    }

    // Target parent is the DND Collection (not allowed)
    const targetCollection = target.collection
        ? getCollectionFromOrgAllContent(allContent, target.collection)
        : undefined;
    if (dndCollection.item.collection.id === targetCollection?.item.collection.id) {
        return true;
    }

    return (
        targetCollection?.ancestors.some(
            (ancestor) => ancestor.collection.id === dndCollection.item.collection.id
        ) ?? false
    );
}

/**
 * Returns true if content can be inserted into the given owner. This
 * function is designed to prevent going too deep in content nesting.
 */
function canInsertInOwner(
    owner: ContentOwnership,
    allContent: api.OrganizationAllContent
): boolean {
    if (!owner.collection) {
        return true;
    }

    const collection = getCollectionFromOrgAllContent(allContent, owner.collection);
    if (!collection) {
        return false;
    }

    // top-level is 0, so allow insertions up to 2 levels deep
    return collection.ancestors.length < COLLECTION_MAX_DEPTH;
}

/**
 * Factors a SpacePointer or CollectionPointer given their type and a content key
 */
export function getContentPointer(
    type: api.SpacePointer['type'] | api.CollectionPointer['type'],
    key: string
): api.SpacePointer | api.CollectionPointer {
    return type === 'space'
        ? {
              type,
              space: key,
          }
        : {
              type,
              collection: key,
          };
}
